import { useCallback, useMemo } from 'react';
import {
  Configuration,
  DefaultApi,
  type VesselResult,
} from '@ais-ui/api-spec/shipdb';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';

import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import {
  convertFieldsToDates,
  getAuthHeaderFromCredentials,
} from '@ais-ui/utils';
import { getBasePath } from './getAISBasePath';
import { useApiClientWrapper } from './apiClientWrapper';
import { useSession } from '@ais-ui/websockets';
import { useBasePath } from '@ais-ui/custom-hooks';

const useShipDbApi = () => {
  const credentials = useCredentials();
  const apiClientWrapper = useApiClientWrapper();

  const { hostname } = useBasePath();
  const basePath = getBasePath();
  const { sessionId } = useSession();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/shipdb`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    configuration = new Configuration({
      basePath: `${basePath}aisweb/api/shipdb`,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const shipDbApi = useMemo(
    () => new DefaultApi(configuration),
    [configuration]
  );
  return apiClientWrapper(shipDbApi) as DefaultApi;
};

export const useGetShipByImo = (
  imo: number,
  enabled = true
): UseQueryResult<VesselResult> => {
  const shipDbApi = useShipDbApi();
  return useQuery({
    queryKey: ['getShipByImoShipDb', imo],
    queryFn: async () => {
      if (imo === 0) {
        return null;
      }

      const result = await shipDbApi.readByImoImoVesselImoNumberGet({
        vesselImoNumber: imo,
      });

      if (result) {
        convertFieldsToDates(result, [
          'basic.portCalls.ata',
          'basic.portCalls.atd',
          'advancedVessel.keelLaidDate',
          'advancedRegister.beneficialOwnerEffectiveDate',
          'advancedRegister.classificationSocietyEffectiveDate',
          'advancedRegister.ismManagerEffectiveDate',
          'advancedRegister.registeredOwnerEffectiveDate',
          'advancedRegister.shipManagerEffectiveDate',
          'advancedIntegrity.inspections.detention_end_date',
          'advancedIntegrity.inspections.detention_begin_date',
          'advancedIntegrity.inspections.inspection_date',
          'advancedIntegrity.bans.ban_begin_date',
          'advancedIntegrity.bans.ban_end_date',
          'advancedIntegrity.sanctions.sanction_begin_date',
          'advancedIntegrity.sanctions.sanction_end_date',
        ]);
      }

      return result;
    },
    enabled,
  }, 'shipDb:getByImo');
};

export const useGetShipByImoFunction = () => {
  const shipDbApi = useShipDbApi();
  const queryClient = useQueryClient();

  const fetchShipByImo = useCallback(
    async (imo: number): Promise<VesselResult | null> => {
      if (imo === 0) return null;

      const result = await shipDbApi.readByImoImoVesselImoNumberGet({
        vesselImoNumber: imo,
      });

      if (result) {
        convertFieldsToDates(result, [
          'basic.portCalls.ata',
          'basic.portCalls.atd',
          'advancedVessel.keelLaidDate',
          'advancedRegister.beneficialOwnerEffectiveDate',
          'advancedRegister.classificationSocietyEffectiveDate',
          'advancedRegister.ismManagerEffectiveDate',
          'advancedRegister.registeredOwnerEffectiveDate',
          'advancedRegister.shipManagerEffectiveDate',
          'advancedIntegrity.inspections.detention_end_date',
          'advancedIntegrity.inspections.detention_begin_date',
          'advancedIntegrity.inspections.inspection_date',
          'advancedIntegrity.bans.ban_begin_date',
          'advancedIntegrity.bans.ban_end_date',
          'advancedIntegrity.sanctions.sanction_begin_date',
          'advancedIntegrity.sanctions.sanction_end_date',
        ]);
      }

      return result;
    },
    [shipDbApi]
  );

  const getShipByImo = useCallback(
    async (imo: number) => {
      const queryKey = ['getShipByImoShipDb', imo];
      return queryClient.fetchQuery({
        queryKey,
        queryFn: () => fetchShipByImo(imo),
      });
    },
    [queryClient, fetchShipByImo]
  );

  return useMemo(() => ({ getShipByImo }), [getShipByImo]);
};

export const useGetShipByMmsi = (
  mmsi: number,
  enabled = true
): UseQueryResult<VesselResult> => {
  const shipDbApi = useShipDbApi();

  return useQuery({
    queryKey: ['getShipByMmsiShipDb', mmsi],
    queryFn: async () => {
      if (mmsi === 0) {
        return null;
      }

      const result = await shipDbApi.readByMmsiMmsiVesselMmsiNumberGet({
        vesselMmsiNumber: mmsi,
      });

      if (result) {
        convertFieldsToDates(result, [
          'basic.portCalls.ata',
          'basic.portCalls.atd',
          'advancedVessel.keelLaidDate',
          'advancedRegister.beneficialOwnerEffectiveDate',
          'advancedRegister.classificationSocietyEffectiveDate',
          'advancedRegister.ismManagerEffectiveDate',
          'advancedRegister.registeredOwnerEffectiveDate',
          'advancedRegister.shipManagerEffectiveDate',
          'advancedIntegrity.inspections.detention_end_date',
          'advancedIntegrity.inspections.detention_begin_date',
          'advancedIntegrity.inspections.inspection_date',
          'advancedIntegrity.bans.ban_begin_date',
          'advancedIntegrity.bans.ban_end_date',
          'advancedIntegrity.sanctions.sanction_begin_date',
          'advancedIntegrity.sanctions.sanction_end_date',
        ]);
      }

      return result;
    },
    enabled,
  }, 'shipDb:getByMmsi');
};

export const useFetchShipByMmsi = () => {
  const shipDbApi = useShipDbApi();

  const fetchShipByMmsi = useCallback(
    async (mmsi: number): Promise<VesselResult | null> => {
      if (mmsi === 0) {
        return null;
      }

      const result = await shipDbApi.readByMmsiMmsiVesselMmsiNumberGet({
        vesselMmsiNumber: mmsi,
      });

      if (result) {
        convertFieldsToDates(result, [
          'basic.portCalls.ata',
          'basic.portCalls.atd',
          'advancedVessel.keelLaidDate',
          'advancedRegister.beneficialOwnerEffectiveDate',
          'advancedRegister.classificationSocietyEffectiveDate',
          'advancedRegister.ismManagerEffectiveDate',
          'advancedRegister.registeredOwnerEffectiveDate',
          'advancedRegister.shipManagerEffectiveDate',
          'advancedIntegrity.inspections.detention_end_date',
          'advancedIntegrity.inspections.detention_begin_date',
          'advancedIntegrity.inspections.inspection_date',
          'advancedIntegrity.bans.ban_begin_date',
          'advancedIntegrity.bans.ban_end_date',
          'advancedIntegrity.sanctions.sanction_begin_date',
          'advancedIntegrity.sanctions.sanction_end_date',
        ]);
      }

      return result;
    },
    [shipDbApi]
  );

  return fetchShipByMmsi;
};

export const useShipDbFilterTokens = (enabled = true) => {
  const shipDbApi = useShipDbApi();

  const fetchShipDbFilterToken = async (): Promise<any> => {
    const result = await shipDbApi.filterTokensGet();
    return result;
  };

  return useQuery({
    queryKey: ['shipDbFilterToken'],
    queryFn: fetchShipDbFilterToken,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled,
  }, 'shipDb:filterTokens');
};
